// Self-update of the desktop app (Tauri updater plugin, see installer/README.md).
// Runs from the app's start, signed in or not (a fault that stops the sign-in must stay repairable by an update, found
// 04.11.2026): the sign-in screen shows the update itself, the main window takes over with its toasts.
// Checks on start and every 6 h when „Automatisch nach Updates suchen“ is on, offers the update in a toast
// („Update 1.2.0 verfügbar · Jetzt installieren“), shows the download progress, installs and relaunches.
// „Automatisch installieren“ downloads in the background and only asks for the restart.
// Failures stay quiet (no update server reachable → note in Präferenzen); only a manual check reports them.
// Browser: everything here is a no-op (isDesktop()).
import { getVersion } from '@tauri-apps/api/app';
import { relaunch } from '@tauri-apps/plugin-process';
import { check, type Update } from '@tauri-apps/plugin-updater';
import { tr2 } from '../i18n/lang';
import { isDesktop, platformName } from '../platform';

export const CHECK_EVERY_MS = 6 * 60 * 60 * 1000;
/** First automatic check a little after start, so login and the first data load go first. */
const FIRST_CHECK_MS = 20_000;
const LAST_CHECK_KEY = 'rimon:update:lastCheck';
/** The switches live in the person's preferences on the server: this copy holds them for the time before the sign-in. */
const OPTS_KEY = 'rimon:update:opts';

/** How the main window shows updater messages (its design toast with one action button). */
export interface UpdaterUi {
  toast(text: string, kind: 'success' | 'warning', action?: () => void, ms?: number, actionLabel?: string): void;
  /** State changed (Präferenzen re-render). */
  changed(): void;
}

export interface UpdaterState {
  version: string;
  checking: boolean;
  lastCheck: number | null;
  /** Version on the server, when newer than the installed one. */
  available: string | null;
  /** 0–100 while downloading, null otherwise. */
  progress: number | null;
  /** Downloaded and installed (macOS) / downloaded (Windows), waiting for the restart. */
  ready: boolean;
  /** Last check failed (server not reachable, bad signature, …). */
  failed: boolean;
}

const state: UpdaterState = { version: '', checking: false, lastCheck: readLastCheck(), available: null, progress: null, ready: false, failed: false };
let opts = readOpts();
const listeners = new Set<() => void>();
let ui: UpdaterUi | null = null;
let pending: Update | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let first: ReturnType<typeof setTimeout> | null = null;
let busy: Promise<void> | null = null;

function readOpts(): { check: boolean; auto: boolean } {
  try { const o = JSON.parse(localStorage.getItem(OPTS_KEY) ?? '{}') as { check?: boolean; auto?: boolean }; return { check: o.check !== false, auto: !!o.auto }; } catch { return { check: true, auto: false }; }
}
function readLastCheck(): number | null {
  try { const v = Number(localStorage.getItem(LAST_CHECK_KEY)); return v > 0 ? v : null; } catch { return null; }
}
function writeLastCheck(t: number) {
  state.lastCheck = t;
  try { localStorage.setItem(LAST_CHECK_KEY, String(t)); } catch { /* private storage: keep it in memory */ }
}
const changed = () => { ui?.changed(); listeners.forEach((f) => f()); };
/** The sign-in screen follows the state without a main window. Returns the stop function. */
export function onUpdaterChange(f: () => void): () => void { listeners.add(f); return () => { listeners.delete(f); }; }

export const updaterState = (): Readonly<UpdaterState> => state;

/** Is a check due? (Automatic checks only; at most every 6 h across restarts.) */
export function checkDue(lastCheck: number | null, now: number, every = CHECK_EVERY_MS): boolean {
  return lastCheck == null || now - lastCheck >= every || lastCheck > now;
}

/** Download percentage from the plugin's byte counters (null while the size is unknown). */
export function percent(done: number, total: number | undefined): number | null {
  if (!total || total <= 0) return null;
  return Math.max(0, Math.min(100, Math.floor((done / total) * 100)));
}

/**
 * App start (main.tsx), main window only: the schedule, independent of the sign-in. While nobody is signed in the first
 * check ignores the 6 h pause – this may be the update that repairs the sign-in.
 */
export function bootUpdater(hash = location.hash) {
  if (!isDesktop() || timer || /^#\/(pay-assistant|tray)/.test(hash)) return;
  getVersion().then((v) => { state.version = v; changed(); }, () => {});
  first = setTimeout(() => { if (opts.check && (!ui || checkDue(state.lastCheck, Date.now()))) void checkNow(false); }, FIRST_CHECK_MS);
  timer = setInterval(() => { if (opts.check) void checkNow(false); }, CHECK_EVERY_MS);
}

/** Main window mounted: it shows the updater's messages from now on, also what was found before. Returns the stop function. */
export function startUpdater(target: UpdaterUi): () => void {
  if (!isDesktop()) return () => {};
  ui = target;
  if (state.progress == null && state.ready) offerRestart();
  else if (state.progress == null && pending) offer(pending.version);
  return () => { ui = null; };
}

/** Präferenzen → Updates. */
export function configureUpdater(next: { check: boolean; auto: boolean }) {
  const wasAuto = opts.auto;
  opts = next;
  try { localStorage.setItem(OPTS_KEY, JSON.stringify(next)); } catch { /* private storage: the next sign-in brings them again */ }
  // Switched to automatic install while an update waits → fetch it now.
  if (!wasAuto && next.auto && pending && !state.ready && state.progress == null) void download(true);
}

/** Asks the update server. `manual`: from the button in Präferenzen, reports „aktuell“ and errors. */
export function checkNow(manual: boolean): Promise<void> {
  if (!isDesktop()) return Promise.resolve();
  if (busy) return busy;
  busy = (async () => {
    state.checking = true;
    changed();
    try {
      const update = await check({ timeout: 30_000 });
      state.failed = false;
      writeLastCheck(Date.now());
      if (!update) {
        state.available = null;
        if (manual) ui?.toast(tr2('RimoCompta ist aktuell (Version ' + state.version + ').', 'RimoCompta est à jour (version ' + state.version + ').'), 'success');
        return;
      }
      pending = update;
      state.available = update.version;
      if (state.ready) return offerRestart();
      if (opts.auto && !manual) return void download(true);
      offer(update.version);
    } catch (e) {
      state.failed = true;
      writeLastCheck(Date.now());
      if (manual) ui?.toast(tr2('Update-Server nicht erreichbar.', 'Serveur de mise à jour injoignable.'), 'warning');
      else console.info('[updater] check failed', e);
    } finally {
      state.checking = false;
      busy = null;
      changed();
    }
  })();
  return busy;
}

function offer(version: string) {
  ui?.toast(tr2('Update ' + version + ' verfügbar', 'Mise à jour ' + version + ' disponible'), 'success', () => void download(false), 60_000, tr2('Jetzt installieren', 'Installer maintenant'));
}

function offerRestart() {
  const win = platformName() === 'windows';
  ui?.toast(
    tr2('Update ' + state.available + (win ? ' bereit' : ' installiert'), 'Mise à jour ' + state.available + (win ? ' prête' : ' installée')),
    'success', () => void finish(), 60_000,
    win ? tr2('Jetzt installieren', 'Installer maintenant') : tr2('Neu starten', 'Redémarrer'),
  );
}

/** Downloads (and on macOS installs) the pending update. `background`: no progress toasts, ask only for the restart. */
async function download(background: boolean): Promise<void> {
  const update = pending;
  if (!update || state.progress != null) return;
  let done = 0, total: number | undefined;
  state.progress = 0;
  changed();
  try {
    await update.download((ev) => {
      if (ev.event === 'Started') total = ev.data.contentLength;
      else if (ev.event === 'Progress') {
        done += ev.data.chunkLength;
        const p = percent(done, total);
        if (p != null && p !== state.progress) {
          state.progress = p;
          if (!background) ui?.toast(tr2('Update ' + update.version + ' wird geladen · ' + p + ' %', 'Téléchargement de la mise à jour ' + update.version + ' · ' + p + ' %'), 'success', undefined, 60_000);
          changed();
        }
      }
    });
    state.progress = null;
    // Windows: installing closes the app (NSIS/MSI) → only when the user says so. macOS: swap the bundle now.
    if (platformName() === 'windows') {
      state.ready = true;
      if (background) offerRestart(); else await finish();
      return;
    }
    await update.install();
    state.ready = true;
    if (background) offerRestart(); else await finish();
  } catch (e) {
    state.progress = null;
    console.warn('[updater] install failed', e);
    ui?.toast(tr2('Update konnte nicht installiert werden.', 'La mise à jour n\'a pas pu être installée.'), 'warning');
  } finally {
    changed();
  }
}

/** Windows: run the downloaded installer (it quits and restarts the app). macOS: relaunch into the new bundle. */
async function finish(): Promise<void> {
  try {
    if (platformName() === 'windows' && pending) await pending.install();
    ui?.toast(tr2('RimoCompta startet neu …', 'RimoCompta redémarre …'), 'success');
    await relaunch();
  } catch (e) {
    console.warn('[updater] relaunch failed', e);
    ui?.toast(tr2('Bitte RimoCompta neu starten (⌘Q).', 'Veuillez redémarrer RimoCompta.'), 'warning');
  }
}

/** Präferenzen → Updates → status line under the installed version. */
export function statusText(s: Readonly<UpdaterState>, fmtTime: (t: number) => string): string {
  if (s.progress != null) return tr2('Update ' + s.available + ' wird geladen · ' + s.progress + ' %', 'Téléchargement ' + s.available + ' · ' + s.progress + ' %');
  if (s.ready) return tr2('Update ' + s.available + ' bereit · wird beim Neustart aktiv.', 'Mise à jour ' + s.available + ' prête · active au redémarrage.');
  if (s.checking) return tr2('Suche nach Updates …', 'Recherche de mises à jour …');
  const last = s.lastCheck ? tr2('Zuletzt geprüft: ', 'Dernière vérification : ') + fmtTime(s.lastCheck) : tr2('Noch nicht geprüft.', 'Pas encore vérifié.');
  if (s.failed) return last + tr2(' · Kein Update-Server erreichbar.', ' · Aucun serveur de mise à jour joignable.');
  if (s.available) return last + tr2(' · Update ' + s.available + ' verfügbar.', ' · Mise à jour ' + s.available + ' disponible.');
  return last + (s.lastCheck ? tr2(' · Aktuell.', ' · À jour.') : '');
}

/** Präferenzen button: install a found update, otherwise check. */
export function primaryAction(): { label: string; run: () => void; busy: boolean } {
  const busyNow = state.checking || state.progress != null;
  if (state.ready) return { label: platformName() === 'windows' ? tr2('Jetzt installieren', 'Installer maintenant') : tr2('Neu starten', 'Redémarrer'), run: () => void finish(), busy: busyNow };
  if (state.available && pending) return { label: tr2('Update ' + state.available + ' installieren', 'Installer ' + state.available), run: () => void download(false), busy: busyNow };
  return { label: tr2('Jetzt nach Updates suchen', 'Rechercher des mises à jour'), run: () => void checkNow(true), busy: busyNow };
}
